import { compressToUTF16, decompressFromUTF16 } from 'async-lz-string';
import { debounce } from 'es-toolkit';
import { produce } from 'immer';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { mapEligibleMainPractitionersToAvailableProfessionals } from '@/utils/chain-config-utils.ts';
import { BookingStep } from '@/utils/constants.js';
import { dayjs } from '@/utils/dayjsSetup.js';

import localForage from './services/localforage.js';

const isCompressionRequired = !localForage.supports(localForage.INDEXEDDB);

const SESSION_LIFETIME = 3600000; // 1 hour lifetime

export const useAppointmentStore = create((set, get) => ({
    session: JSON.parse(localStorage.getItem('appointmentSearch')),
    setSession: (newState) => {
        if (get().bookFromDoctor || !get().sessionChecked || get().disabled) return;
        set({ session: { ...newState, validUntil: Date.now() + SESSION_LIFETIME } });
        localStorage.setItem('appointmentSearch', JSON.stringify(newState));
    },
    bookFromDoctor: false,
    disabled: true,
    setBookFromDoctor: (value) => set({ bookFromDoctor: value }),
    isSavedInsuranceUsed: false,
    insuranceTypeContext: '',
    clearSession: () => {
        set({ session: {} });
        localStorage.removeItem('appointmentSearch');
    },
    sessionChecked: false,
}));

export const useCookiesStore = create(
    persist(
        () => ({
            accepted: null,
            essentialCookies: false,
            analyticsCookies: false,
            functionalCookies: false,
        }),
        {
            name: 'cookies-storage',
        }
    )
);

export const useNotificationsStore = create((set) => ({
    hideSmsNotificationCard: localStorage.getItem('hideSmsNotificationCard'),
    setHideSmsNotificationCard: () => {
        localStorage.setItem('hideSmsNotificationCard', 'true');
        set({ hideSmsNotificationCard: 'true' });
    },
    showSuccessMessage: false,
}));

export const useLoggingStore = create(() => ({
    context: {},
    largeText: '',
    smallText: '',
    ctaText: '',
}));

export const usePrismicStore = create(() => ({
    customers: null,
    customersById: null,
    pageNotFoundData: null,
    unsubscribeData: null,
}));

export const useFormLocation = create(
    persist(
        () => ({
            hub: {},
            secondary_location: {},
            dc_instance_id: '',
        }),
        {
            name: 'formlocation-storage', // Storage key
        }
    )
);

export const useCustomAnamnesisStore = create((set) => ({
    questions: {},
    subQuestions: {},
    questionsLang: null,
    anamnesisTemplate: null,
    currentCategory: null,
    completedCategories: {},
    loading: true,
    hasFailed: false,
    answers: {},
    highlightedQuestions: {},
    highlightedQuestionForScrolling: null,
    prismicData: {},
    storedProfessionalId: null,
    storedTemplateId: null,
    loginRedirect: false,
    professional: null,
    isPreview: false,
    isFormSent: false,
    redirectToConsentForms: false,
    formsToFill: null,
    currentForm: null,
    guestFormData: [],
    resetGuestFormData: () => set({ guestFormData: [] }),
}));

// Spread wherever only the suggestion paging is reset (Back / re-validate) without dropping the chain choice.
// chainSuggestionHistory is the ChainAvailabilityItem[][] back-stack; its top is the page shown before the last Next.
export const CLEARED_CHAIN_PAGING = {
    chainSuggestionHistory: [],
    chainAvailability: [],
};

// Spread wherever `selectedChainConfig` is cleared so the chain-funnel fields reset together.
export const CLEARED_CHAIN_SELECTION = {
    selectedChainConfig: null,
    chainIsReturningPatient: null,
    chainMainPractitionerCandidate: null,
    // { type: 'specific'; id: string } | { type: 'no_preference' } | null (null = not yet chosen).
    chainMainPractitioner: null,
    ...CLEARED_CHAIN_PAGING,
};

export const useSearchStore = create(() => ({
    prismicData: null,
    selectedHealthInsurance: null,
    selectedLocation: null,
    selectedSpecialty: null,
    selectedProfessional: null,
    locations: null,
    professionals: null,
    specialties: null,
    treatments: null,
    treatmentTypeCategories: null,
    search: '',
    selectedTreatments: {},
    // Lives here (not useBookingStore) so it survives resetStore() during the patient-type click.
    selectedChainConfig: null,
    // Chain patient type + main-practitioner candidate; kept here (not useBookingStore) to survive resetStore().
    chainIsReturningPatient: null,
    chainMainPractitionerCandidate: null,
    // { type: 'specific'; id: string } | { type: 'no_preference' } | null (null = not yet chosen).
    chainMainPractitioner: null,
    chainSuggestionHistory: [],
    chainAvailability: [],
    allDocuments: null,
    showAvailableProfessionals: false,
    isOnlineAppointment: false,
    availableProfessionals: [],
    queryValuesSet: false,
}));

export const selectSingleTreatment = (treatmentKey, isOnline = false) => {
    useSearchStore.setState({
        selectedTreatments: { [treatmentKey]: true },
        ...CLEARED_CHAIN_SELECTION,
        showAvailableProfessionals: true,
        isOnlineAppointment: isOnline,
    });
};

export const selectChainConfig = (config) => {
    useSearchStore.setState({
        selectedTreatments: {},
        ...CLEARED_CHAIN_SELECTION,
        selectedChainConfig: config,
        isOnlineAppointment: false,
        availableProfessionals: mapEligibleMainPractitionersToAvailableProfessionals(config),
        showAvailableProfessionals: true,
    });
};

export const useConfigStore = create(() => ({
    currentCustomer: null,
    showPrismicFallback: false,
}));

export const useProfessionalsStore = create(() => ({
    mainDataArr: null,
    mainDataArrActive: null,
    mainDataMyProfessionals: {
        'en-us': [],
        'de-de': [],
    },
    mainDataMyProfessionalsID: [],
    allProfessionals: {
        'en-us': [],
        'de-de': [],
    },
    shuffledMyProfessionals: [],
    hubProfessionals: [],
    professionalMappings: null,
    professionalMappingsById: null,
}));

export const useHeaderStore = create(() => ({
    headerDoctor: '',
    headerDoctorKey: '',
}));

export const usePrimarySecondaryOverrideStore = create(
    persist(
        () => ({
            primaryColor: null,
            secondaryColor: null,
        }),
        {
            name: 'dev-primary-secondary-override',
        }
    )
);

const initialBookingStoreState = {
    currentStep: BookingStep.TIMESLOT_SELECTION,
    selectedProfessional: null,
    professionalDetails: null,
    showReturningPatientToggle: true,
    showPatientTypeSelection: true,
    isReturningPatient: false,
    isOnlineAppointment: false,
    selectedTimeslot: null,
    registrationFormData: {
        selectedGender: null,
        dob: null,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        isEmailInUse: false,
        previouslyTriedEmail: '',
        street: '',
        houseNumber: '',
        zipCode: '',
        city: '',
        country: 'DE',
        verificationCode: '',
        isConsentConfirmed: false,
        isAnalyticsConfirmed: false,
    },
    loading: false,
    appointmentData: null,
    additionalInfo: '',
    showBookingModal: false,
    showLoginModal: false,
    signUpStarted: false,
    calendarStartDate: dayjs().format('YYYY-MM-DD'),
    isCodeIncorrect: false,
    previouslyTriedCode: '',
    targetId: null,
    pvsProvider: null,
    isProfessionalSelectionSkipped: true,
    consentTreatments: null,
    bookedAppointmentData: null,
    // Set when a chain collection is booked, so the confirmation page can hydrate full appointment
    // details for the collection. The outcome itself (chainBookingFailed) is already known synchronously
    // from the book-collection response, not from this fetch.
    bookedCollectionId: '',
    chainBookingFailed: false,
    isChainConfirmation: false,
    invalidTimeSlot: null,
    isSignatureEmpty: false,
    isChainSuggestionFetching: false,
};

export const useBookingStore = create((set) => ({
    ...initialBookingStoreState,
    resetStore: () => set({ ...initialBookingStoreState }),
    setSelectedGender: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.selectedGender = value;
            })
        ),
    setDob: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.dob = value;
            })
        ),
    setFirstName: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.firstName = value;
            })
        ),
    setLastName: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.lastName = value;
            })
        ),
    setEmail: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.email = value;
            })
        ),
    setPhoneNumber: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.phoneNumber = value;
            })
        ),
    setPassword: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.password = value;
            })
        ),
    setConfirmPassword: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.confirmPassword = value;
            })
        ),
    setEmailCheck: (isEmailInUse, previouslyTriedEmail) =>
        set(
            produce((state) => {
                state.registrationFormData.isEmailInUse = isEmailInUse;
                state.registrationFormData.previouslyTriedEmail = previouslyTriedEmail;
            })
        ),
    setStreet: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.street = value;
            })
        ),
    setHouseNumber: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.houseNumber = value;
            })
        ),
    setZipCode: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.zipCode = value;
            })
        ),
    setCity: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.city = value;
            })
        ),
    setCountry: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.country = value;
            })
        ),
    setVerificationCode: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.verificationCode = value;
            })
        ),
    setVerificationCodeCheck: (isCodeIncorrect, previouslyTriedCode) =>
        set(
            produce((state) => {
                state.isCodeIncorrect = isCodeIncorrect;
                state.previouslyTriedCode = previouslyTriedCode;
            })
        ),
    setIsConsentConfirmed: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.isConsentConfirmed = value;
            })
        ),
    setIsAnalyticsConfirmed: (value) =>
        set(
            produce((state) => {
                state.registrationFormData.isAnalyticsConfirmed = value;
            })
        ),
}));

// Configure localForage
localForage.config({
    name: 'EternoPatientsApp', // Database name
    storeName: 'prismicStore', // Object store name
});

const storage = createJSONStorage(() => {
    return {
        getItem: () => null,
        setItem: debounce(async (key, value) => {
            let storedValue = value;
            if (isCompressionRequired) {
                storedValue = await compressToUTF16(value);
            }

            await localForage.setItem(key, storedValue);
        }, 2000),
        removeItem: async (key) => {
            await localForage.removeItem(key);
        },
    };
});

const initialPersistedPrismicStoreState = {
    // Prismic Caching
    appVersion: null,
    timestamp: undefined,
    ref: undefined,

    // Header and FooterData
    footerData: {
        en: { content: {}, aboutMenu: {}, legalMenu: {}, dataSet: false },
        de: { content: {}, aboutMenu: {}, legalMenu: {}, dataSet: false },
    },
    headerData: {
        en: { contentHeading: {}, mainNav: {}, dataSet: false },
        de: { contentHeading: {}, mainNav: {}, dataSet: false },
    },

    // Confirm Appointment
    confirmAppointmentData: { en: undefined, de: undefined },

    // Static Pages
    staticPages: { en: undefined, de: undefined },

    // Consent Forms
    consentFormsTreatment: { en: undefined, de: undefined },
    treatmentContractWelcomeData: { en: undefined, de: undefined },

    // Cookie Consent
    cookieConsentData: { en: undefined, de: undefined },

    // Welcome
    welcomePageAnamnesisData: { data: undefined, dataSet: false },

    // Herakles Data
    heraklesAllForms: { en: undefined, de: undefined },
    heraklesCommunication: { en: undefined, de: undefined },
    heraklesCourse: { en: undefined, de: undefined },
    heraklesTherapy: { en: undefined, de: undefined },
    heraklesFormsCheckin: { en: undefined, de: undefined },

    // Wieland Forms
    wielandFormsThankYou: { en: undefined, de: undefined },

    // Login
    loginData: { en: { content: undefined, dataSet: false }, de: { content: undefined, dataSet: false } },

    // Registration
    registerData: { en: { dataSet: false, benefits: undefined }, de: { dataSet: false, benefits: undefined } },

    // Doctor Data
    doctorData: { en: { content: {}, dataSet: false }, de: { content: {}, dataSet: false } },

    // Professional Profile Data
    professionalProfileData: {
        en: { mainData: undefined, content: [], dataSet: false },
        de: { mainData: undefined, content: [], dataSet: false },
    },

    // health profile
    healthProfileData: {
        en: { content: {}, dataArr: {}, dataSet: false },
        de: { content: {}, dataArr: {}, dataSet: false },
    },

    // medical records data
    medicalRecordsData: { en: { content: undefined, dataSet: false }, de: { content: undefined, dataSet: false } },

    // Dashboard
    dashboardData: { en: { content: {}, dataSet: false }, de: { content: {}, dataSet: false } },

    // Account
    accountData: { en: { content: {}, dataSet: false }, de: { content: {}, dataSet: false } },

    // Location Data
    locationData: {
        en: { content: {}, dataSet: false, allAppointmentsPrismicData: undefined },
        de: { content: {}, dataSet: false, allAppointmentsPrismicData: undefined },
    },

    // Health Data
    healthData: {
        en: { content: {}, dataSet: false, mainDataProfessionalDoctors: [] },
        de: { content: {}, dataSet: false, mainDataProfessionalDoctors: [] },
    },

    // Anamnesis Data
    anamnesisData: {
        en: {
            allData: {},
            mainDataProfessionalDoctors: {},
            mainDataAccountEterno: {},
            mainDataSummary: [],
            mainData: {},
            mainDataAccount: {},
            contentProfessional: [],
            welcomeData: {},
            thankYouData: {},
            dataSet: false,
        },
        de: {
            allData: {},
            mainDataProfessionalDoctors: {},
            mainDataAccountEterno: {},
            mainDataSummary: [],
            mainData: {},
            mainDataAccount: {},
            contentProfessional: [],
            welcomeData: {},
            thankYouData: {},
            dataSet: false,
        },
    },

    // Book Appointment Data
    bookAppointmentData: {
        en: { mainData: undefined, dataSet: false },
        de: { mainData: undefined, dataSet: false },
    },

    // Consent Data
    consentData: {
        en: {
            content: {},
            loading: false,
        },
        de: {
            content: {},
            loading: false,
        },
    },

    // Appointment data
    appointmentData: {
        en: { content: {}, dataSet: false },
        de: { content: {}, dataSet: false },
    },

    findAppointmentData: {
        en: {
            content: {},
            contentHeading: [],
            bookingHeading: {},
            professionalProfiles: [],
            dataSet: false,
            unprocessedData: null,
        },
        de: {
            content: {},
            contentHeading: [],
            bookingHeading: {},
            professionalProfiles: [],
            dataSet: false,
            unprocessedData: null,
        },
    },

    // Feature Flags
    featureFlags: {},
};

export const usePersistedPrismicStore = create(
    persist(
        (set) => ({
            ...initialPersistedPrismicStoreState,
            _hasHydrated: false,
            resetStore: () => set({ ...initialPersistedPrismicStoreState }),
            loadStore: async () => {
                const value = await localForage.getItem('prismic-storage');

                if (!value) {
                    set({ _hasHydrated: true });
                    return;
                }

                if (isCompressionRequired && typeof value === 'string') {
                    const decompressed = await decompressFromUTF16(value);
                    set({ ...JSON.parse(decompressed).state, _hasHydrated: true });
                    return;
                }

                if (typeof value === 'string') {
                    const parsedValue = JSON.parse(value);
                    set({ ...parsedValue.state, _hasHydrated: true });
                    return;
                }

                set({ ...value.state, _hasHydrated: true });
            },
        }),
        {
            name: 'prismic-storage',
            storage: storage,
        }
    )
);

export const useUploadDocumentModalStore = create((set) => ({
    appointmentId: undefined,
    isModalOpen: false,
    isDocumentUploaded: false,
    openModal: (appointmentId) => set({ appointmentId: appointmentId, isModalOpen: true }),
    closeModal: () =>
        set({
            appointmentId: undefined,
            isModalOpen: false,
            isDocumentUploaded: false,
        }),
}));

export const useXundStore = create((set) => ({
    appointmentId: undefined,
    locationId: undefined,
    linked_form_code: undefined,
    isXundTriage: false,
    isXundTriageCompleted: false,
    isXundTriageProcessed: false,
    setData: (appointmentId, locationId, linked_form_code) =>
        set({
            appointmentId,
            locationId,
            linked_form_code,
        }),
    clearData: () =>
        set({
            appointmentId: undefined,
            locationId: undefined,
            linked_form_code: undefined,
        }),
}));

export const resetSearchSelection = () => {
    useSearchStore.setState({
        search: '',
        selectedTreatments: {},
        selectedProfessional: null,
        selectedSpecialty: null,
        showAvailableProfessionals: false,
        availableProfessionals: [],
        isOnlineAppointment: false,
        ...CLEARED_CHAIN_SELECTION,
    });
    useXundStore.setState({ isXundTriage: false, isXundTriageCompleted: false, isXundTriageProcessed: false });
};

export const useDownloadDocumentModalStore = create((set) => ({
    isModalOpen: false,
    documentId: undefined,
    openModal: (documentId) => set({ isModalOpen: true, documentId: documentId }),
    closeModal: () => set({ isModalOpen: false, documentId: undefined }),
}));

export const useSharedFormsStore = create(() => ({
    sharedForms: null,
}));

export const useUserLinkingStore = create(() => ({
    code: undefined,
    userId: undefined,
    locationKey: undefined,
    personalData: undefined,
    isUserLinked: false,
    isCurrentFormFirst: false,
    entrySource: undefined,
}));

export const useAppointmentActionStore = create((set, get) => ({
    appointmentId: undefined, // string | undefined - appointment to act on
    action: undefined, // one of AppointmentActions | undefined
    highlightedAppointmentId: undefined, // string | undefined - appointment to highlight
    highlightTimer: undefined, // timeout ID for auto-clearing highlight

    setAction: (appointmentId, action) => set({ appointmentId, action }),
    clearAction: () => set({ appointmentId: undefined, action: undefined }),

    // Set highlighted appointment with auto-clearing after timeout
    setHighlightedAppointment: (id, duration = 6000) => {
        // Clear any existing timer
        const currentTimer = get().highlightTimer;
        if (currentTimer) {
            clearTimeout(currentTimer);
        }

        // Set the highlighted ID
        set({ highlightedAppointmentId: id });

        // Set timer to clear highlight after duration
        const timer = setTimeout(() => {
            set({ highlightedAppointmentId: undefined });
        }, duration);

        // Store timer ID
        set({ highlightTimer: timer });
    },

    clearHighlight: () => {
        const currentTimer = get().highlightTimer;
        if (currentTimer) {
            clearTimeout(currentTimer);
        }
        set({ highlightedAppointmentId: undefined, highlightTimer: undefined });
    },
}));

export const useRegisterStore = create((set) => ({
    is_registered_from_code: false,
    registration_code: null,
    setIsRegisteredFromCode: (value) => set({ is_registered_from_code: value }),
    setRegistrationCode: (code) => set({ registration_code: code }),
    resetRegisterStore: () => set({ is_registered_from_code: false, registration_code: null }),
}));
